// src/components/dashboards/add_patient_v2/AddPatientFormV2.tsx
// -----------------------------------------------------------------------------
// AddPatientFormV2 — orchestrator for the V2 cards UI.
//
// Composition:
//   - <PatientCard/> for each entry in the cards array.
//   - <AddPatientsDropzone/> at the bottom for bulk additions + manual "+ add".
//   - Intent dialog for 2+ PDFs dropped in the global zone (combine vs split).
//   - Environment multi-select (mirrors V1 patterns, same data-tour anchor).
//   - Single submit button → POST /api/patients/bulk-upload/start/ with the
//     extended schema (per-patient external_id + description) we shipped in
//     Phase 1.
//
// Design reference: cursor_logs/030 § 2026-05-22 cards-UI design.
// V1 (AddPatientForm.tsx) remains untouched.
// -----------------------------------------------------------------------------

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Groups as GroupsIcon,
  PersonAdd as PersonAddIcon,
} from "@mui/icons-material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useDropzone, FileWithPath } from "react-dropzone";

import { useTranslation } from "../../../i18n";
import logger from "utils/logger";
import { Environment, PlatformUser } from "types";
import { useFeatureGate } from "../../../hooks/useFeatureGate";
import { useAuth } from "../../../hooks/useAuth";
import { useTutorial } from "../../../hooks/useOnboardingTutorial";
import CreateEnvironmentModal from "../CreateEnvironmentModal";
import {
  athenaDarkActionsSx,
  athenaDarkContentSx,
  athenaDarkPaperSx,
  athenaDarkPrimaryButtonSx,
  athenaDarkSecondaryButtonSx,
  athenaDarkTitleSx,
  athenaColors,
} from "../../layout/AthenaDialogStyles";

import PatientCard from "./components/PatientCard";
import AddPatientsDropzone, {
  classifyDrop,
  FolderNode,
  flattenFolderFiles,
  hasSubfolders,
} from "./components/AddPatientsDropzone";
import EnvChipSelector from "./components/EnvChipSelector";
import FolderLevelPickerDialog from "./components/FolderLevelPickerDialog";
import TabularWizardModal from "./components/TabularWizardModal";
import { makeHighlighter, zoneElForTarget } from "./dnd/dndShared";
import { useFileDropGuard } from "./dnd/useFileDropGuard";
import {
  useCardsState,
  pruneEmptyLeadingCards,
  isCardEmpty,
} from "./hooks/useCardsState";
import { useSubmitCard } from "./hooks/useSubmitCard";
import { GlobalDropKind, PatientCardData } from "./types";
import { StagedTabularPatient } from "../TabularWizard";

/** Convert react-dropzone's flat FileWithPath[] (folders flattened, each file
 *  carrying a `path` like "Anna_Garcia/scan.pdf") back into the loose/folders
 *  shape classifyDrop expects. */
function rdzGroup(files: FileWithPath[]): {
  loose: File[];
  folders: Record<string, File[]>;
  tree: FolderNode[];
} {
  const loose: File[] = [];
  const folders: Record<string, File[]> = {};
  // Build a nested FolderNode tree so the folder-level picker can detect a
  // single dropped folder that contains subfolders (synthetic_patients/ with
  // Patient_01/, Patient_02/ …) and ask where the patient boundary sits. This
  // mirrors the folder-PICKER path's tree-building in AddPatientsDropzone's
  // handleFolderPickerChange. It is pure post-processing of files react-dropzone
  // already delivered — it does NOT touch the drag-drop mechanism itself.
  const treeMap = new Map<string, FolderNode>();
  const ensureNode = (parent: FolderNode | null, name: string): FolderNode => {
    if (parent) {
      let child = parent.subfolders.find((s) => s.name === name);
      if (!child) {
        child = { name, files: [], subfolders: [] };
        parent.subfolders.push(child);
      }
      return child;
    }
    let root = treeMap.get(name);
    if (!root) {
      root = { name, files: [], subfolders: [] };
      treeMap.set(name, root);
    }
    return root;
  };
  for (const f of files) {
    const rel = (f.path || f.name).replace(/^\.?\//, "");
    const parts = rel.split("/").filter(Boolean);
    if (parts.length > 1) {
      const top = parts[0];
      (folders[top] = folders[top] || []).push(f);
      // Walk path segments, creating directory nodes; the last segment is the
      // file, which lands in the deepest directory node.
      let node = ensureNode(null, top);
      for (let p = 1; p < parts.length - 1; p++) {
        node = ensureNode(node, parts[p]);
      }
      node.files.push(f);
    } else {
      loose.push(f);
    }
  }
  return { loose, folders, tree: Array.from(treeMap.values()) };
}

// =============================================================================
// Props — kept signature-compatible with V1 so the parent can swap forms.
// =============================================================================

export interface AddPatientFormV2Props {
  newPatient: Partial<PlatformUser>;
  setNewPatient: (patient: Partial<PlatformUser>) => void;
  availableEnvironments: Environment[];
  selectedEnvIds: string[];
  setSelectedEnvIds: (ids: string[]) => void;
  onCreate: (files: File[]) => Promise<boolean>;
  onBulkComplete?: () => void;
  onClose?: () => void;
  onEnvironmentCreated?: (env: { id: string; name: string }) => void;
}

// =============================================================================
// Component
// =============================================================================

const AddPatientFormV2: React.FC<AddPatientFormV2Props> = ({
  availableEnvironments,
  selectedEnvIds,
  setSelectedEnvIds,
  onBulkComplete,
  // onClose is intentionally not consumed here: as of cursor_logs/036 §34,
  // "Create all" stays on the page (cards stream their state in place) instead
  // of collapsing the form. The dashboard keeps its own collapse control.
  onEnvironmentCreated,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { quotas } = useFeatureGate();
  const { user: currentUser } = useAuth();
  // Onboarding tutorial context. V1 wired setTutorialPatient on its
  // create-success path (SystemAdministratorDashboard.handleCreatePatient);
  // V2 creates via useSubmitCard and never invoked that handler, so the
  // tutorial lost the created-patient reference (used later by the processing
  // seed + the search step's "we typed <name>" greeting). We re-wire it below
  // by watching the first card's succeeded transition while the tutorial runs.
  const { isActive: tutorialActive, setTutorialPatient } = useTutorial();

  // Mirror V1's env-creation quota gate so the dropdown's first row shows a
  // lock when the user is at their tier ceiling.
  const maxEnvironments = quotas.max_environments;
  const atEnvironmentLimit =
    maxEnvironments > 0 && availableEnvironments.length >= maxEnvironments;
  const [createEnvOpen, setCreateEnvOpen] = useState(false);

  const {
    cards,
    updateCard,
    appendFilesToCard,
    addCards,
    removeCard,
    dropEmptyLeadingCards,
    idleCards,
    allIdleCardsValid,
  } = useCardsState();
  const { submitCard, submitBulk } = useSubmitCard({ updateCard, tutorialActive });

  // ── Tutorial: record the created patient (mirrors V1) ─────────────────────
  // While the onboarding tutorial is running, capture the first card that
  // reaches `succeeded` with a real createdPatientId and hand it to the
  // tutorial context. This is what V1's handleCreatePatient did via
  // tutorial.setTutorialPatient(name, id). The tutorial's processing step seeds
  // mock data into this patient and the search step greets the user by name.
  // Fires at most once per form mount (guarded by ref) so a later batch
  // creation doesn't overwrite the tutorial's patient reference.
  const tutorialPatientRecordedRef = useRef(false);
  useEffect(() => {
    if (!tutorialActive || tutorialPatientRecordedRef.current) return;
    const succeeded = cards.find(
      (c) => c.status === "succeeded" && c.createdPatientId,
    );
    if (succeeded && succeeded.createdPatientId) {
      const idNum = Number(succeeded.createdPatientId);
      if (!Number.isNaN(idNum)) {
        tutorialPatientRecordedRef.current = true;
        setTutorialPatient(
          succeeded.name.trim() || `Patient ${idNum}`,
          idNum,
        );
        // A1 — Auto-navigate after the tutorial's V2 create (Joan, task A).
        // V1 navigated to /patients/{id}/details on create-success so the
        // tutorial's patient-details steps ([data-tour='table-sidebar'] etc.)
        // could find their targets. V2 normally stays on the dashboard (shows
        // a "Go to patient" link), which auto-skips those steps. We replicate
        // V1's SPA nav, but STRICTLY when the tutorial is active — normal
        // multi-card flows keep the "Go to patient" link and are untouched.
        navigate(`/patients/${idNum}/details`);
      }
    }
  }, [cards, tutorialActive, setTutorialPatient, navigate]);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // PDF-intent dialog state — triggered when 2+ PDFs are dropped in the
  // global zone (one patient per file vs combine into one).
  const [pdfIntentOpen, setPdfIntentOpen] = useState(false);
  const [pendingPdfFiles, setPendingPdfFiles] = useState<File[]>([]);

  // TabularWizard modal — opens when a CSV/Excel file is dropped in the
  // global zone. The wizard manages its own parse/schema/poll lifecycle;
  // we just provide the open/close/complete plumbing. The dropped file is
  // handed to the wizard via `tabularInitialFile` so the user doesn't have
  // to re-select it (Joan, 2026-05-27).
  const [tabularModalOpen, setTabularModalOpen] = useState(false);
  const [tabularInitialFile, setTabularInitialFile] = useState<File | null>(
    null,
  );

  // Folder-level picker — opens when a dropped folder contains subfolders.
  // We ask the user whether the patient boundary sits at the dropped root
  // (one patient) or at the subfolder level (one patient per subfolder).
  // See cursor_logs entry on the synthetic_patients/ confusion.
  const [folderPickerOpen, setFolderPickerOpen] = useState(false);
  const [folderPickerRoot, setFolderPickerRoot] = useState<FolderNode | null>(
    null,
  );

  // Auto-select the first environment if none is selected (mirrors V1).
  useEffect(() => {
    if (availableEnvironments.length > 0 && selectedEnvIds.length === 0) {
      setSelectedEnvIds([availableEnvironments[0].id]);
    }
  }, [availableEnvironments, selectedEnvIds.length, setSelectedEnvIds]);

  // A card the user is actively typing in is never auto-removed, even while it
  // still reads as empty. Uses the same `data-patient-card-id` marker the drop
  // router hit-tests, so there is one way to map a DOM node to a card.
  const isCardFocused = useCallback((card: PatientCardData) => {
    const active = document.activeElement;
    if (!active) return false;
    const host = active.closest?.("[data-patient-card-id]");
    return host?.getAttribute("data-patient-card-id") === card.id;
  }, []);

  // A drop lands its cards AFTER the pristine card the form mounts with, so the
  // first box is left empty and blocks Create-all until the user clicks its ✕.
  // Both calls are functional setState, so this sees the just-added cards.
  const addCardsFromDrop = useCallback(
    (newCards: Partial<PatientCardData>[]) => {
      addCards(newCards);
      dropEmptyLeadingCards(isCardFocused);
    },
    [addCards, dropEmptyLeadingCards, isCardFocused],
  );

  // ── Drop routing ────────────────────────────────────────────────────────
  const materializeFolderCards = useCallback(
    (foldersByTopLevel: Record<string, File[]>) => {
      const newCards: Partial<PatientCardData>[] = Object.entries(
        foldersByTopLevel,
      ).map(([folderName, files]) => ({
        name: folderName.replace(/_/g, " ").trim(),
        files,
      }));
      addCardsFromDrop(newCards);
      enqueueSnackbar(
        t(
          "patients_v2.folders_appended",
          "{count} patient cards added from folders.",
        ).replace("{count}", String(newCards.length)),
        { variant: "success", autoHideDuration: 4000 },
      );
    },
    [addCardsFromDrop, enqueueSnackbar, t],
  );

  const materializePdfsAsCards = useCallback(
    (files: File[]) => {
      const newCards: Partial<PatientCardData>[] = files.map((f) => ({
        // Strip extension; the LLM will refine the name from the doc content.
        // For now, the filename stem is a reasonable placeholder.
        name: f.name.replace(/\.[^.]+$/, ""),
        files: [f],
      }));
      addCardsFromDrop(newCards);
    },
    [addCardsFromDrop],
  );

  const handleGlobalDropClassified = useCallback(
    (result: GlobalDropKind) => {
      switch (result.kind) {
        case "empty":
          return;
        case "mixed":
          enqueueSnackbar(
            t(
              "patients_v2.mixed_drop_blocked",
              "Drop one type at a time: either PDFs, or folders, or a spreadsheet — not a mix.",
            ),
            { variant: "warning", autoHideDuration: 6000 },
          );
          return;
        case "folders": {
          // If the drop has tree info AND any single top-level folder
          // contains subfolders, open the level picker so the user can say
          // where the patient boundary sits (synthetic_patients/ vs
          // synthetic_patients/Patient_01/). Multi-folder drops without
          // nested structure flow through as before.
          const nestedRoots = (result.tree ?? []).filter((n) => hasSubfolders(n));
          if (nestedRoots.length === 1 && (result.tree ?? []).length === 1) {
            setFolderPickerRoot(nestedRoots[0]);
            setFolderPickerOpen(true);
            return;
          }
          materializeFolderCards(result.foldersByTopLevel);
          return;
        }
        case "zip":
          // Phase 3 scaffold: ZIP is accepted as a single-file card. The
          // unpack-then-1-card-per-folder semantics is a follow-up.
          addCardsFromDrop([
            { name: result.file.name.replace(/\.zip$/i, ""), files: [result.file] },
          ]);
          enqueueSnackbar(
            t(
              "patients_v2.zip_added_as_card",
              "ZIP added as a single patient card. Hand-edit if you intended one patient per folder inside.",
            ),
            { variant: "info", autoHideDuration: 6000 },
          );
          return;
        case "tabular": {
          // Open the wizard modal. It manages its own parse / schema /
          // polling lifecycle and uses the cached upload_id on the server.
          // The file isn't re-sent through V2's bulk-upload pipeline; the
          // wizard's own /bulk-upload/start-tabular/ endpoint handles it.
          if (!selectedEnvIds[0] && availableEnvironments[0]) {
            setSelectedEnvIds([availableEnvironments[0].id]);
          }
          if (!selectedEnvIds[0] && !availableEnvironments[0]) {
            enqueueSnackbar(
              t(
                "patients_v2.no_environment_error",
                "Select an environment before creating patients.",
              ),
              { variant: "warning", autoHideDuration: 6000 },
            );
            return;
          }
          // Hand the dropped spreadsheet straight to the wizard so it parses
          // immediately instead of re-prompting for a file.
          setTabularInitialFile(result.file);
          setTabularModalOpen(true);
          return;
        }
        case "pdfs": {
          if (result.files.length === 1) {
            materializePdfsAsCards(result.files);
          } else {
            // Multiple PDFs: ask the user.
            setPendingPdfFiles(result.files);
            setPdfIntentOpen(true);
          }
          return;
        }
      }
    },
    [
      addCardsFromDrop,
      availableEnvironments,
      enqueueSnackbar,
      materializeFolderCards,
      materializePdfsAsCards,
      selectedEnvIds,
      setSelectedEnvIds,
      t,
    ],
  );

  const handlePdfIntentSplit = () => {
    materializePdfsAsCards(pendingPdfFiles);
    setPdfIntentOpen(false);
    setPendingPdfFiles([]);
  };
  const handlePdfIntentCombine = () => {
    addCardsFromDrop([{ files: pendingPdfFiles }]);
    setPdfIntentOpen(false);
    setPendingPdfFiles([]);
  };

  const closeFolderPicker = useCallback(() => {
    setFolderPickerOpen(false);
    setFolderPickerRoot(null);
  }, []);

  const handleFolderPickRoot = useCallback(
    (folder: FolderNode) => {
      // Materialize the whole root as a single patient.
      materializeFolderCards({
        [folder.name]: flattenFolderFiles(folder),
      });
      closeFolderPicker();
    },
    [closeFolderPicker, materializeFolderCards],
  );

  const handleFolderPickSubfolders = useCallback(
    (subfolders: FolderNode[], looseRootFiles: File[]) => {
      const dict: Record<string, File[]> = {};
      for (const sub of subfolders) {
        dict[sub.name] = flattenFolderFiles(sub);
      }
      // Loose files that sat directly in the dropped root become their own
      // card so nothing is silently dropped.
      if (looseRootFiles.length > 0) {
        const rootName = folderPickerRoot?.name || "Root";
        dict[`${rootName} (loose files)`] = looseRootFiles;
      }
      materializeFolderCards(dict);
      closeFolderPicker();
    },
    [closeFolderPicker, folderPickerRoot, materializeFolderCards],
  );

  const handleAddManualCard = useCallback(() => {
    addCards();
  }, [addCards]);

  // Stable per-card submit. Re-resolves envId via a ref so updates to
  // selectedEnvIds don't invalidate the callback identity — keeping
  // React.memo on PatientCard effective.
  const envResolutionRef = useRef<{ selected: string[]; available: Environment[] }>({
    selected: selectedEnvIds,
    available: availableEnvironments,
  });
  useEffect(() => {
    envResolutionRef.current = {
      selected: selectedEnvIds,
      available: availableEnvironments,
    };
  }, [selectedEnvIds, availableEnvironments]);

  const handleSubmitCard = useCallback(
    (card: PatientCardData) => {
      const { selected, available } = envResolutionRef.current;
      const envId = selected[0] || available[0]?.id || "";
      if (!envId) {
        setSubmitError(
          t(
            "patients_v2.no_environment_error",
            "Select an environment before creating patients.",
          ),
        );
        return;
      }
      // Multi-env targeting (cursor_logs/043): pass every ticked env; envId is
      // the primary (index 0) used for matching/dedup.
      const envIds = selected.length > 0 ? selected : [envId];
      setSubmitError(null);
      void submitCard(card, envId, envIds);
    },
    [submitCard, t],
  );

  // Activate a cross-environment patient's env (cursor_logs/036 §35). Promote
  // it to the FRONT of the selection so it becomes the creation env
  // (selectedEnvIds[0] is what bulk-upload/start uses), which is what lets the
  // attach land on the existing patient instead of spawning a duplicate. Warn
  // that this re-targets the whole session: every patient created now goes to
  // this env. No-op if already the creation env.
  const handleActivateEnvironment = useCallback(
    (envId: string) => {
      // cursor_logs/044: the incoming id comes from the patient-name
      // suggestion path, which String()-coerces ids, while /environments/
      // serves NUMERIC ids (the TS `Environment.id: string` is aspirational).
      // Resolve to the CANONICAL id from availableEnvironments via a
      // String-coerced match so a foreign-typed id never enters the
      // selection — strict `.includes()` in EnvChipSelector and the prefs
      // reconcile would silently drop it (env activated but checkbox
      // unchecked; selection lost on reload).
      const env = availableEnvironments.find(
        (e) => String(e.id) === String(envId),
      );
      const canonicalId = env ? env.id : envId;
      if (String(selectedEnvIds[0]) === String(canonicalId)) return;
      setSelectedEnvIds([
        canonicalId,
        ...selectedEnvIds.filter((id) => String(id) !== String(canonicalId)),
      ]);
      enqueueSnackbar(
        t(
          "patients_v2.cross_env_activated",
          "Now creating in {env}. Every patient created in this session will be added to it.",
        ).replace("{env}", env?.name || String(envId)),
        { variant: "info", autoHideDuration: 6000 },
      );
    },
    [selectedEnvIds, setSelectedEnvIds, availableEnvironments, enqueueSnackbar, t],
  );

  // ── Submit ──────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (selectedEnvIds.length === 0 && availableEnvironments.length > 0) {
      setSelectedEnvIds([availableEnvironments[0].id]);
    }
    const envId = selectedEnvIds[0] || availableEnvironments[0]?.id;
    if (!envId) {
      setSubmitError(
        t(
          "patients_v2.no_environment_error",
          "Select an environment before creating patients.",
        ),
      );
      return;
    }

    // Guard again here, not just on drop: the drop may have been routed while
    // a card was focused (vetoed then), or the user may have cleared the first
    // card by hand afterwards. Either way a stale empty leading card must not
    // fail the name check below and block the whole batch.
    const cardsToCreate = pruneEmptyLeadingCards(cards);
    if (cardsToCreate.length !== cards.length) dropEmptyLeadingCards();

    // Validate per-card names; surface inline.
    const invalidIds = cardsToCreate
      .filter((c) => c.name.trim().length === 0)
      .map((c) => c.id);
    if (invalidIds.length > 0) {
      invalidIds.forEach((id) => updateCard(id, { nameMissing: true }));
      setSubmitError(
        t(
          "patients_v2.name_missing_error",
          "Every patient needs a name. Fill the highlighted cards.",
        ),
      );
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      // Only submit cards in `idle` status. Succeeded/submitting/polling/
      // failed cards are intentionally skipped — see cursor_logs/030
      // § 2026-05-22 per-card streaming creation, locked Q&A #2.
      const idleCardsToSubmit = cardsToCreate.filter(
        (c) => c.status === "idle",
      );

      // Joan, 2026-06-04 (cursor_logs/036 §34): stay on the page after
      // "Create all". submitBulk fires ONE batch job (mass-tabular stays a
      // single job + single notification) and streams per-patient status back
      // onto each card via patient_index, exactly like the per-card path. No
      // reset(), no onClose() — the cards transition idle → polling →
      // succeeded/failed in place so the user watches the batch progress.
      // Multi-env targeting (cursor_logs/043): every ticked env; envId primary.
      const envIds = selectedEnvIds.length > 0 ? selectedEnvIds : [envId];
      const jobId = await submitBulk(idleCardsToSubmit, envId, envIds);

      if (jobId) {
        enqueueSnackbar(
          t(
            "patients_v2.submit_success",
            "{count} patient(s) submitted for processing.",
          ).replace("{count}", String(idleCardsToSubmit.length)),
          { variant: "success", autoHideDuration: 5000 },
        );
        // Refresh the dashboard patient list in the background, but keep the
        // form open so the cards can show live state.
        onBulkComplete?.();
      } else {
        const msg = t(
          "patients_v2.submit_failed",
          "Submit failed. Please try again.",
        );
        setSubmitError(msg);
        enqueueSnackbar(msg, { variant: "error", autoHideDuration: 6000 });
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || (err as Error)?.message || "Submit failed";
      logger.error("[AddPatientFormV2] submit failed", err);
      setSubmitError(msg);
      enqueueSnackbar(msg, { variant: "error", autoHideDuration: 6000 });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────
  const idleCount = idleCards.length;

  // The set that Create-all actually operates on. An empty leading card is
  // dropped on submit regardless, so it must not gate the button either, or the
  // user is stuck looking at a disabled Create with no visible reason.
  // Focus is deliberately NOT vetoed here: clicking Create is an explicit
  // instruction to proceed, unlike the automatic on-drop cleanup.
  const effectiveCards = useMemo(() => pruneEmptyLeadingCards(cards), [cards]);
  const effectiveIdleCards = useMemo(
    () => effectiveCards.filter((c) => c.status === "idle"),
    [effectiveCards],
  );
  const effectiveIdleValid = useMemo(
    () => effectiveIdleCards.every((c) => c.name.trim().length > 0),
    [effectiveIdleCards],
  );
  // Bulk gate (cursor_logs/036 §28): block "Create all" while ANY idle card's
  // external_id collision probe is still in flight (or has an uncommitted
  // draft). This is the bulk counterpart of the per-card submit-after-probe
  // gate — without it a user could fire the batch before a collision surfaces
  // and the backend would silently merge. PatientCard lifts `externalIdPending`
  // into card state for exactly this check.
  const anyExternalIdPending = cards.some(
    (c) => c.status === "idle" && c.externalIdPending,
  );
  // Block the batch if any idle card has a cross-patient external_id conflict
  // (behaviour D) — the per-card Create button already blocks, but the bulk
  // button must too (cursor_logs/036 §30).
  const anyExternalIdConflict = cards.some(
    (c) => c.status === "idle" && c.externalIdConflict,
  );
  // Block the batch while any idle card is waiting for its environment to be
  // activated (cross-env pick). The per-card Create is already disabled; the
  // bulk button must be too, else Create-all would try to attach in the wrong
  // env and spawn a duplicate. See cursor_logs/036 §35.
  const anyCrossEnvBlocked = cards.some(
    (c) => c.status === "idle" && c.crossEnvBlocked,
  );
  // Cross-card duplicate external_id: the SAME id committed on two idle cards
  // would create/attach two patients to one id. Detect the tokens that appear
  // on >1 idle card; each affected card shows an inline error and the batch is
  // blocked (§30).
  const duplicateExternalIds = useMemo(() => {
    const counts = new Map<string, number>();
    for (const c of cards) {
      if (c.status !== "idle") continue;
      const toks = new Set(
        (c.externalId || "")
          .split(",")
          .map((s) => s.trim().toLowerCase())
          .filter(Boolean),
      );
      for (const t of toks) counts.set(t, (counts.get(t) || 0) + 1);
    }
    return Array.from(counts.entries())
      .filter(([, n]) => n > 1)
      .map(([t]) => t);
  }, [cards]);
  const hasDuplicateExternalIds = duplicateExternalIds.length > 0;
  // Batch button submits idle cards only (joan, 2026-05-22: failed cards
  // are retried per-card; succeeded cards are read-only).
  const submitDisabled =
    submitting ||
    !effectiveIdleValid ||
    effectiveIdleCards.length === 0 ||
    anyExternalIdPending ||
    anyExternalIdConflict ||
    anyCrossEnvBlocked ||
    hasDuplicateExternalIds;

  // ── Centralized drag-drop routing (Joan, 2026-05-27) ─────────────────────
  //
  // The previous design had THREE competing drop handlers — per-card React
  // handlers, form-level React handlers, and a document-level native guard —
  // racing through event propagation. That produced the flaky "sometimes it
  // takes the files, sometimes it doesn't" behaviour, dropping on a button
  // inside a card routing to the global zone, and the outer card lighting up
  // while hovering an inner card.
  //
  // This replaces all of it with ONE document-level handler that decides the
  // target by HIT-TESTING the element under the pointer (closest
  // [data-patient-card-id]) rather than relying on propagation:
  //   - drop inside a card (anywhere, incl. its buttons) → that card
  //   - drop anywhere else → the global / bulk classifier
  //   - a modal is open → bail entirely so the modal's own drop zone wins
  //     (fixes the spreadsheet wizard activating the form behind it)
  //
  const formRootRef = useRef<HTMLDivElement | null>(null);

  // ── Drag-drop (react-dropzone) ────────────────────────────────────────────
  // Drops are handled by react-dropzone (wired below): its file-selector does
  // the robust cross-browser file + folder extraction. The window-level guard
  // below stops the browser's native default (open the file / navigate away)
  // when a file is dropped just off-target. Real-file drops succeed reliably;
  // the only remaining failures are the Wayland/XWayland Nautilus bug, where the
  // OS hands the browser a text path instead of the file — handled by the
  // no-file-drop fallback (auto-opens the picker) further down.
  useFileDropGuard();

  const submittingRef = useRef(submitting);
  submittingRef.current = submitting;

  const onCardFiles = useCallback(
    (cardId: string, files: File[]) => {
      if (files.length === 0) return;
      const result = appendFilesToCard(cardId, files);
      if (result === "succeeded") {
        enqueueSnackbar(
          t(
            "patients_v2.succeeded_card_drop_blocked",
            "This patient was already created. To add more documents, open the patient's detail page from the link in this card.",
          ),
          { variant: "info", autoHideDuration: 6000 },
        );
      } else if (result === "midflight") {
        // The patient is still being created — saying "already created" here
        // (the old behaviour) was wrong and confusing.
        enqueueSnackbar(
          t(
            "patients_v2.midflight_card_drop_blocked",
            "This patient is still being created. Once it finishes, add more documents from its detail page.",
          ),
          { variant: "info", autoHideDuration: 6000 },
        );
      }
      // "ok" (files appended) and "noop" (no matching card) show no toast.
    },
    [appendFilesToCard, enqueueSnackbar, t],
  );

  // ── No-file-drop guidance (Wayland/XWayland Nautilus drag bug) ────────────
  // On a GNOME Wayland session, dragging a file from Nautilus into an XWayland
  // Chrome intermittently delivers only the `text/plain` path string, never the
  // file bytes. No web app can recover those — but a SILENT failure is what made
  // this feel random and broken. We watch every drop landing inside the form and,
  // when it carried no file payload, point the user at the upload button instead.
  // Hidden fallback picker: opened automatically when a drag arrives with no
  // file payload, so a failed Wayland/XWayland drag becomes a one-click file
  // selection routed through the same classifier as a real drop.
  const fallbackInputRef = useRef<HTMLInputElement>(null);
  // If the failed drag was aimed at a specific card, route the fallback pick to
  // that card; otherwise classify globally (create cards). Set at drop time.
  const fallbackCardIdRef = useRef<string | null>(null);
  const handleFallbackPick = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        const arr = Array.from(files);
        const cardId = fallbackCardIdRef.current;
        if (cardId) {
          onCardFiles(cardId, arr);
        } else {
          handleGlobalDropClassified(classifyDrop(arr, {}));
        }
      }
      fallbackCardIdRef.current = null;
      e.target.value = "";
    },
    [handleGlobalDropClassified, onCardFiles],
  );

  const lastNoFileMsgRef = useRef(0);
  useEffect(() => {
    const onWindowDrop = (e: DragEvent) => {
      const root = formRootRef.current;
      const tgt = e.target as Node | null;
      if (!root || !tgt) return;
      const dt = e.dataTransfer;
      if (!dt) return;
      const items = dt.items ? Array.from(dt.items) : [];
      const hasFile =
        (dt.files && dt.files.length > 0) ||
        items.some((it) => it.kind === "file");

      // ── Drops OUTSIDE the form (cursor_logs/075 §4.1) ────────────────────
      // Joan, 2026-05-26: dropping a few pixels off the form canvas must not
      // lose the files. react-dropzone's zone IS the form root, so nothing
      // else will ever see this drop, and useFileDropGuard has already
      // preventDefault'ed the browser's own handling. Without this branch the
      // files are discarded in total silence, which is the exact failure mode
      // the in-form no-payload path below exists to avoid.
      if (!root.contains(tgt)) {
        // dt.files ONLY, deliberately. Reconstructing a dropped FOLDER out
        // here would mean reviving the hand-rolled entry reader that was
        // removed for getting Linux drags wrong (see "Strategy 6" below), and
        // reintroducing that flakiness in a recovery path would defeat its
        // purpose. Loose files are the reported case and are unambiguous.
        const stray = dt.files ? Array.from(dt.files) : [];
        if (stray.length > 0) {
          handleGlobalDropClassified(classifyDrop(stray, {}));
        }
        return;
      }

      if (hasFile) return; // a real file drop — leave it to the drop handlers
      const types = Array.from(dt.types || []);
      // Ignore non-drag-of-content drops (e.g. dragging text inside an input).
      const looksLikeFileAttempt =
        types.includes("text/uri-list") ||
        types.includes("text/plain") ||
        types.length === 0;
      if (!looksLikeFileAttempt) return;
      // Throttle ONLY the snackbar so rapid retries don't stack toasts. The
      // file dialog must open on EVERY failed drop — it's a direct response to
      // the user's gesture, and suppressing it is what made retries do nothing.
      const nowTs = Date.now();
      if (nowTs - lastNoFileMsgRef.current >= 4000) {
        lastNoFileMsgRef.current = nowTs;
        enqueueSnackbar(
          t(
            "patients_v2.drop_no_files",
            "That drag didn't carry a file. On Linux, dragging from the Files manager sometimes drops only a text path. Use the Upload button to pick the files instead.",
          ),
          { variant: "warning", autoHideDuration: 7000 },
        );
      }
      // Remember which card (if any) the drag was aimed at, so the fallback
      // pick lands on the same card instead of creating new ones.
      const cardEl = (tgt as HTMLElement).closest?.("[data-patient-card-id]");
      fallbackCardIdRef.current = cardEl
        ? cardEl.getAttribute("data-patient-card-id")
        : null;
      // The drop is a user gesture, so we can open the native file dialog now.
      fallbackInputRef.current?.click();
    };
    // Capture phase so we see the drop regardless of which handler consumes it.
    window.addEventListener("drop", onWindowDrop, true);
    return () => window.removeEventListener("drop", onWindowDrop, true);
  }, [formRootRef, enqueueSnackbar, handleGlobalDropClassified, t]);

  // ── Strategy 6: react-dropzone ───────────────────────────────────────────
  // One dropzone on the form root (noClick — clicking is handled by the
  // explicit pickers). react-dropzone's file-selector does the robust,
  // cross-browser file/folder extraction (the part our hand-rolled readers
  // kept getting wrong on Linux). We route card-vs-bulk via the drop event's
  // target, and reconstruct the folders dict from each file's `path`.
  const onRdzDrop = useCallback(
    (accepted: FileWithPath[], _rej: unknown, event?: unknown) => {
      if (submitting || accepted.length === 0) return;
      const target = (event as DragEvent | undefined)?.target as
        | HTMLElement
        | undefined;
      const cardEl = target?.closest?.("[data-patient-card-id]") as
        | HTMLElement
        | null;
      if (cardEl) {
        onCardFiles(cardEl.getAttribute("data-patient-card-id") || "", accepted);
        return;
      }
      const { loose, folders, tree } = rdzGroup(accepted);
      handleGlobalDropClassified(classifyDrop(loose, folders, tree));
    },
    [submitting, onCardFiles, handleGlobalDropClassified],
  );

  const {
    getRootProps: getRdzRootProps,
    getInputProps: getRdzInputProps,
  } = useDropzone({
    noClick: true,
    noKeyboard: true,
    multiple: true,
    useFsAccessApi: false, // use the classic drag-drop + file-selector path
    onDrop: onRdzDrop,
  });

  // DOM-only highlighter (no React state during drag). We pass our own
  // onDragOver/onDragEnter/onDragLeave to getRootProps; react-dropzone COMPOSES
  // them with its internal handlers, so its drop logic still runs while we paint
  // the zone outline (card or bulk) under the pointer.
  const rdzHl = useRef(makeHighlighter()).current;

  // react-dropzone root props, merging its ref with formRootRef (below) and
  // adding the highlight handlers.
  const rdzRootProps = getRdzRootProps({
    onDragOver: (e: React.DragEvent) => {
      rdzHl.set(zoneElForTarget(e.target, formRootRef.current));
    },
    onDragEnter: (e: React.DragEvent) => {
      rdzHl.set(zoneElForTarget(e.target, formRootRef.current));
    },
    onDragLeave: (e: React.DragEvent) => {
      const rt = e.relatedTarget as Node | null;
      if (!rt || !e.currentTarget.contains(rt)) rdzHl.clear();
    },
    onDrop: () => rdzHl.clear(),
  }) as Record<string, unknown>;
  const rdzInnerRef = (rdzRootProps?.ref ?? null) as
    | React.MutableRefObject<HTMLElement | null>
    | ((n: HTMLElement | null) => void)
    | null;
  const mergedFormRef = useCallback(
    (node: HTMLDivElement | null) => {
      formRootRef.current = node;
      if (typeof rdzInnerRef === "function") rdzInnerRef(node);
      else if (rdzInnerRef) rdzInnerRef.current = node;
    },
    [rdzInnerRef],
  );

  return (
    // Shared inner gutter — all rows (env chip, cards, add-patient row,
    // submit row) hang on the same 24px column. This is the single grid
    // origin Joan asked for; everything inside should not re-pad.
    //
    // The outer Box is a faint navy-tinted glass canvas that visually anchors
    // the form to the rest of OncoCraft (which uses the same family of
    // GlassBox-tinted surfaces).
    <Box
      ref={mergedFormRef}
      // Onboarding tutorial anchor: the "patient-form" step spotlights the
      // whole V2 form. This is the stable root container — present whenever
      // the form is open (the dashboard mounts it via Collapse/unmountOnExit).
      // The overlay then prefills the name into the first card's
      // [data-tour='patient-name'] field. (V1 carried the same anchor.)
      data-tour="patient-creation-form"
      {...(() => {
        // Spread react-dropzone's root props EXCEPT ref (we merge the ref
        // ourselves via mergedFormRef above).
        const { ref: _omit, ...rest } = rdzRootProps as Record<string, unknown>;
        void _omit;
        return rest;
      })()}
      sx={{
        // Joan, 2026-05-27: the V2 form BECOMES the dashboard's main visual
        // container. Drag highlight is now a DOM outline applied directly to
        // the hovered zone (card or bulk) by the DnD strategy — NO React
        // state changes during drag, so nothing re-renders/repaints the blur.
        px: { xs: 2.5, sm: 5 },
        py: { xs: 2, sm: 2.5 },
        background: alpha(
          theme.palette.secondary.main,
          theme.custom?.containerOpacity?.medium || 0.75,
        ),
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        border: `1px solid ${alpha(theme.palette.common.white, 0.35)}`,
        borderRadius: 3,
        boxShadow: `0 1px 2px ${alpha(theme.palette.primary.dark, 0.08)}, 0 6px 18px ${alpha(theme.palette.primary.dark, 0.12)}`,
        // Accent var consumed by the DOM-outline highlight in dndShared.
        "--dnd-accent": theme.palette.primary.main,
      }}
    >
      <Stack spacing={1}>
        {/* Hidden inputs: react-dropzone's own input (required for its drop
            handling) and our fallback picker, auto-opened when a drag arrives
            without a file payload (the Wayland/XWayland Nautilus drag bug). */}
        <input {...getRdzInputProps()} />
        <input
          ref={fallbackInputRef}
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={handleFallbackPick}
        />
        {/* Top bar — env chip aligned to the TOP RIGHT corner of the form
            canvas (Joan, 2026-05-27). The chip itself is V1-style: small,
            info-tinted, with "+ Create new environment" as the first
            dropdown row (locked when the user is at their tier quota). */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            minHeight: 28,
          }}
          {...(availableEnvironments.length > 1
            ? { "data-tour": "select-environment" }
            : {})}
        >
          <EnvChipSelector
            availableEnvironments={availableEnvironments}
            selectedEnvIds={selectedEnvIds}
            onChange={(ids) => setSelectedEnvIds(ids)}
            disabled={submitting}
            atEnvironmentLimit={atEnvironmentLimit}
            onCreateEnvironmentClick={() => setCreateEnvOpen(true)}
            currentUserId={currentUser?.id ? String(currentUser.id) : undefined}
          />
        </Box>

        <CreateEnvironmentModal
          open={createEnvOpen}
          onClose={() => setCreateEnvOpen(false)}
          onCreated={(newEnv) => {
            setCreateEnvOpen(false);
            if (onEnvironmentCreated) onEnvironmentCreated(newEnv);
            setSelectedEnvIds([newEnv.id]);
            enqueueSnackbar(
              t(
                "patients.env_created_success",
                `Environment "${newEnv.name}" created`,
              ),
              { variant: "success" },
            );
          }}
        />

        {submitError && (
          <Alert
            severity="error"
            onClose={() => setSubmitError(null)}
            sx={{ py: 0.5 }}
          >
            {submitError}
          </Alert>
        )}

        {/* Cards stack — these are the PRIMARY surfaces. Tight 12px gap
            between siblings to read as a single group. */}
        <Stack spacing={1.5}>
          {cards.map((card, idx) => (
            <PatientCard
              key={card.id}
              index={idx}
              card={card}
              onUpdate={updateCard}
              onRemove={removeCard}
              onSubmit={handleSubmitCard}
              disabled={submitting}
              // A LONE card is removable once it has data: ✕ then resets it
              // (removeCard replaces the last card with a fresh empty one).
              // Hidden on a lone EMPTY card, where there is nothing to clear.
              removable={cards.length > 1 || !isCardEmpty(card)}
              environmentId={
                selectedEnvIds[0] || availableEnvironments[0]?.id || ""
              }
              environmentIds={
                selectedEnvIds.length > 0 ? selectedEnvIds : undefined
              }
              onActivateEnvironment={handleActivateEnvironment}
              duplicateExternalIds={duplicateExternalIds}
            />
          ))}
        </Stack>

        {/* "+ Add another patient" — Joan, 2026-05-27: lives directly UNDER
            the card stack and sits on the SAME indentation column as the
            card's inner "Add more details" pill. Achieved with an invisible
            wrapper that matches the card's px:3 padding. */}
        <Box sx={{ display: "flex", justifyContent: "flex-start", pl: 3 }}>
          <Button
            size="small"
            onClick={handleAddManualCard}
            startIcon={<PersonAddIcon sx={{ fontSize: 14 }} />}
            disabled={submitting}
            sx={{
              // V1-pill parity with "Add more details" — same height,
              // fontSize, hover ramp. Joan, 2026-05-27.
              textTransform: "none",
              fontSize: "0.72rem",
              fontWeight: 500,
              px: 1.25,
              py: 0,
              minHeight: 24,
              height: 24,
              bgcolor: alpha(theme.palette.info.main, 0.1),
              color: theme.palette.info.main,
              borderRadius: "12px",
              border: "1px solid transparent",
              backdropFilter: "blur(8px)",
              transition: "all 0.25s ease",
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.18),
                color: theme.palette.primary.main,
                borderColor: alpha(theme.palette.primary.main, 0.4),
              },
            }}
          >
            {t("patients_v2.add_another_manual", "Add patient")}
          </Button>
        </Box>

        {/* Global file drop zone — V1-style centred cloud + text, no manual-
            add button inside. Drops anywhere on this box (or anywhere on
            the form canvas) route through `handleGlobalDropClassified`. */}
        <AddPatientsDropzone
          onDropClassified={handleGlobalDropClassified}
          disabled={submitting}
        />

        {/* Global submit — renders when there are 2+ idle cards. Gradient
            navy CTA matching PatientDetails' "Generate Report" / "Find
            Trials" round-button treatment, but stretched to pill form
            because this action is the primary commit of the whole form. */}
        {idleCount >= 2 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1,
              pt: 1,
              borderTop: `1px solid ${alpha(theme.palette.secondary.main, 0.35)}`,
            }}
          >
            {/* Inline hint while the bulk button is blocked purely because a
                card's external_id probe hasn't resolved yet (cursor_logs/036
                §28). Tells the user it's a brief wait, not a hard error. */}
            {anyExternalIdPending && allIdleCardsValid && !submitting && (
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <CircularProgress size={12} sx={{ color: "inherit" }} />
                {t(
                  "patients_v2.bulk_checking_ids",
                  "Checking external IDs…",
                )}
              </Typography>
            )}
            <Button
              variant="contained"
              size="small"
              color="primary"
              onClick={handleSubmit}
              disabled={submitDisabled}
              disableElevation
              startIcon={
                submitting ? (
                  <CircularProgress size={14} sx={{ color: "inherit" }} />
                ) : (
                  <GroupsIcon sx={{ fontSize: 16 }} />
                )
              }
              sx={{
                // Standard MUI contained primary button — same scale as the
                // V1 form's "Create Patient" (Joan, 2026-05-25: the big
                // gradient pill was way too prominent vs the rest of the app).
                textTransform: "none",
                fontWeight: 500,
                fontSize: "0.82rem",
                px: 2,
                minHeight: 32,
              }}
            >
              {t(
                "patients_v2.submit_many_remaining",
                "Create {count} patients",
              ).replace("{count}", String(idleCount))}
            </Button>
          </Box>
        )}

      {/* PDF intent dialog — opens when 2+ PDFs are dropped in the global zone.
          Joan, 2026-06-04: switched to the shared athenaDark variant so the
          body copy + the two interactive option buttons render with proper
          contrast on the deep-navy → royal-blue gradient. */}
      <Dialog
        open={pdfIntentOpen}
        onClose={() => {
          setPdfIntentOpen(false);
          setPendingPdfFiles([]);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: athenaDarkPaperSx }}
      >
        <DialogTitle sx={athenaDarkTitleSx}>
          {t(
            "patients_v2.pdf_intent_title",
            "You added {count} PDFs",
          ).replace("{count}", String(pendingPdfFiles.length))}
        </DialogTitle>
        <DialogContent sx={athenaDarkContentSx}>
          <DialogContentText sx={{ mb: 1.5, color: athenaColors.white }}>
            {t(
              "patients_v2.pdf_intent_question",
              "Create one patient per PDF, or combine them into a single patient?",
            )}
          </DialogContentText>
          <Stack spacing={1.25} sx={{ mt: 1 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<GroupsIcon />}
              onClick={handlePdfIntentSplit}
              sx={{
                ...athenaDarkPrimaryButtonSx,
                justifyContent: "flex-start",
                py: 1.25,
                minHeight: 44,
              }}
            >
              {t(
                "patients_v2.pdf_intent_split",
                "{count} patients (one per PDF)",
              ).replace("{count}", String(pendingPdfFiles.length))}
            </Button>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<PersonAddIcon />}
              onClick={handlePdfIntentCombine}
              sx={{
                justifyContent: "flex-start",
                textTransform: "none",
                py: 1.25,
                minHeight: 44,
                color: athenaColors.white,
                borderColor: alpha(athenaColors.periwinkle, 0.5),
                "&:hover": {
                  borderColor: athenaColors.periwinkle,
                  background: alpha(athenaColors.periwinkle, 0.12),
                },
              }}
            >
              {t(
                "patients_v2.pdf_intent_combine",
                "1 patient (combine all PDFs)",
              )}
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={athenaDarkActionsSx}>
          <Button
            onClick={() => {
              setPdfIntentOpen(false);
              setPendingPdfFiles([]);
            }}
            sx={athenaDarkSecondaryButtonSx}
          >
            {t("action.cancel", "Cancel")}
          </Button>
        </DialogActions>
      </Dialog>

      <FolderLevelPickerDialog
        open={folderPickerOpen}
        rootFolder={folderPickerRoot}
        onCancel={closeFolderPicker}
        onChooseRoot={handleFolderPickRoot}
        onChooseSubfolders={handleFolderPickSubfolders}
      />

      {/* TabularWizard modal — opens when a CSV/Excel is dropped on the
          global "Add patients" zone. The wizard owns its own state and
          calls back via onComplete (direct-submit) or onReviewInCards
          (editable cards path) when the user picks one of the two
          parallel actions at the final step. */}
      <TabularWizardModal
        open={tabularModalOpen}
        initialFile={tabularInitialFile}
        onClose={() => {
          setTabularModalOpen(false);
          setTabularInitialFile(null);
        }}
        onComplete={() => {
          setTabularModalOpen(false);
          setTabularInitialFile(null);
          enqueueSnackbar(
            t(
              "patients_v2.tabular_complete",
              "Spreadsheet import finished. Patients are being processed.",
            ),
            { variant: "success", autoHideDuration: 6000 },
          );
          onBulkComplete?.();
        }}
        onReviewInCards={({ uploadId, patients }: { uploadId: string; patients: StagedTabularPatient[] }) => {
          // Materialize N editable cards in the V2 form, each pre-filled
          // from the wizard's staged per-patient data. Cards keep their
          // tabular origin metadata so the bulk endpoint can recreate the
          // synthetic Document with the same prompt the wizard would have
          // built. The modal closes itself.
          const newCardOverrides: Partial<PatientCardData>[] = patients.map(
            (p) => ({
              name: p.name,
              externalId: p.externalId || "",
              description: "",
              files: [],
              source: "tabular" as const,
              uploadId,
              rowIndex: p.rowIndex,
              tabularPromptText: p.tabularPromptText,
              status: "idle" as const,
            }),
          );
          addCardsFromDrop(newCardOverrides);
          enqueueSnackbar(
            t(
              "patients_v2.review_cards_appended",
              "{count} patients ready for review. Edit any card, then click Create.",
            ).replace("{count}", String(patients.length)),
            { variant: "success", autoHideDuration: 6000 },
          );
        }}
        environmentId={selectedEnvIds[0] || availableEnvironments[0]?.id || ""}
        environmentIds={
          selectedEnvIds.length > 0
            ? selectedEnvIds
            : availableEnvironments[0]
              ? [availableEnvironments[0].id]
              : []
        }
      />
      </Stack>
    </Box>
  );
};

export default AddPatientFormV2;
